import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "./AppContext";
import styles from './ChatFrame.module.css';

// Overridable for local development: point at a locally running edulla-chat
// dev server (e.g. http://localhost:3000) via REACT_APP_CHAT_BASE_URL.
const CHAT_BASE_URL = process.env.REACT_APP_CHAT_BASE_URL || "https://bot.app.edulla.ai";
const CHAT_INDEX_PATH = "/index.html";

export const buildIframeUrl = (lastLocation: unknown, tid: string, pageUrl?: string): string => {
  const sanitizedPath = (() => {
    if (typeof lastLocation !== "string") {
      return CHAT_INDEX_PATH;
    }

    const trimmed = lastLocation.trim();

    if (!trimmed || trimmed === "/" || trimmed === "undefined" || trimmed === CHAT_INDEX_PATH) {
      return CHAT_INDEX_PATH;
    }

    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  })();

  const url = new URL(sanitizedPath, CHAT_BASE_URL);

  if (tid) {
    url.searchParams.set("tid", tid);
  } else {
    url.searchParams.delete("tid");
  }

  if (pageUrl) {
    url.searchParams.set("pageurl", pageUrl);
  }

  return url.toString();
};

const isIFrame = (input: HTMLElement | null): input is HTMLIFrameElement =>
  input !== null && input.tagName === 'IFRAME';

function ChatFrame() {
  const { state } = useContext(AppContext);
  const tid = window.sessionStorage.getItem("edullaTid") ?? "";

  const initialSrc = useMemo(() => buildIframeUrl(state.lastLocation, tid, window.location.href), [state.lastLocation, tid]);
  const [iframeSrc, setIframeSrc] = useState(initialSrc);

  useEffect(() => {
    setIframeSrc(buildIframeUrl(state.lastLocation, tid, window.location.href));
  }, [state.lastLocation, tid]);

  // Detect SPA navigation on the parent page and notify the inner frame
  const lastUrlRef = useRef(window.location.href);
  useEffect(() => {
    const checkUrl = setInterval(() => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrlRef.current) {
        lastUrlRef.current = currentUrl;
        const frame = document.getElementById('edullaChatWidgetFrame');
        if (isIFrame(frame) && frame.contentWindow) {
          frame.contentWindow.postMessage(
            { isEdulla: true, action: 'updatePageUrl', data: currentUrl },
            "*"
          );
        }
      }
    }, 1000);
    return () => clearInterval(checkUrl);
  }, []);

  const isChatVisible = state.showChat;

  const wrapperClassName = `${styles.chatFrameWrapper} ${isChatVisible ? styles.chatFrameWrapperShow : styles.chatFrameWrapperHidden}`;
  const isHidden = !isChatVisible;
  const ariaHidden = isHidden || undefined;
  const iframeTabIndex = isHidden ? -1 : undefined;

  return (
    <div className={wrapperClassName} aria-hidden={ariaHidden} hidden={isHidden}>
      <iframe
        className={styles.chatFrame}
        src={iframeSrc}
        id="edullaChatWidgetFrame"
        title="Chat"
        tabIndex={iframeTabIndex}
        aria-hidden={ariaHidden}
        hidden={isHidden}
      ></iframe>
    </div>
  );
}

export default ChatFrame;
